import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Stack, Chip, Button, Paper, Typography } from "@mui/material";
import GlobalWrapper from "../../../components/Wrapper/GlobalWrapper";
import { getJobById, getJobApplications } from "../../../redux/features/careerSlice";

const STATUS = { 0: "Draft", 1: "Published", 2: "Deleted", 3: "Closed" };
const label = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// one labelled field in the info grid
const Field = ({ label: l, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary">{l}</Typography>
    <Typography variant="body2" fontWeight={600}>{value != null && value !== "" ? value : ""}</Typography>
  </Grid>
);

// Officer job detail page: full job info + target batches + eligible/applied counts.
const CareerJobDetail = () => {
  const { jobId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { jobDetail, applicants, detailLoading } = useSelector((state) => state.career);

  useEffect(() => {
    // getJobById now returns eligible_count, so no separate (expensive) eligible-list fetch
    dispatch(getJobById(jobId));
    dispatch(getJobApplications(jobId));
  }, [dispatch, jobId]);

  const job = jobDetail || {};
  const counts = applicants?.meta?.counts || {};
  const eligibleCount = job.eligible_count || 0;
  const targets = job.targets || [];
  const skills = Array.isArray(job.skills) ? job.skills : job.skills ? [].concat(job.skills) : [];

  return (
    <GlobalWrapper
      title={"Job Detail"}
      actions={
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => navigate(`/career-connect/jobs/${jobId}/eligible-students`)}>Who Can Apply</Button>
          <Button variant="outlined" size="small" onClick={() => navigate(`/career-connect/jobs/${jobId}/applicants`)}>Applicants</Button>
          <Button variant="outlined" size="small" onClick={() => navigate("/career-connect/jobs")}>Back</Button>
        </Stack>
      }
    >
      {detailLoading && !jobDetail ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : !jobDetail ? (
        // the job is gone (deleted, or a stale link). Say so, rather than
        // drawing an empty card that looks like a broken page.
        <Typography color="text.secondary">
          This job no longer exists. It may have been deleted.
        </Typography>
      ) : (
        <Box sx={{ width: "100%" }}>
          {/* header */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Box>
                <Typography variant="h6">{job.job_title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {job.company_name || "Institution placement"}
                </Typography>
              </Box>
              <Chip color="primary" label={STATUS[job.job_status] != null ? STATUS[job.job_status] : job.job_status} />
            </Stack>
          </Paper>

          {/* counts */}
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
            <Chip color="primary" variant="outlined" label={`Eligible: ${eligibleCount}`} />
            <Chip color="success" variant="outlined" label={`Applicants: ${counts.total || 0}`} />
            {Object.entries(counts)
              .filter(([k]) => k !== "total")
              .map(([k, v]) => (
                <Chip key={k} variant="outlined" label={`${label(k)}: ${v}`} />
              ))}
          </Stack>

          {/* job info */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Job Information</Typography>
            <Grid container spacing={2}>
              <Field label="Location" value={job.location} />
              <Field label="Work Mode" value={job.work_mode} />
              <Field label="Salary" value={job.salary} />
              <Field label="Stipend" value={job.stipend} />
              <Field label="Vacancies" value={job.vacancies} />
              <Field label="Deadline" value={job.deadline} />
              <Field label="Qualification" value={job.qualification} />
              <Field label="Experience" value={job.experience} />
              <Field label="Gender" value={job.gender} />
              <Field label="Working Hours" value={job.working_hours} />
              <Field label="Duration" value={job.duration} />
              <Field label="Industry" value={job.industry} />
            </Grid>

            {skills.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Skills</Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                  {skills.map((s, i) => <Chip key={i} size="small" label={s} />)}
                </Stack>
              </Box>
            )}

            {job.description && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Description / Instructions</Typography>
                <Typography variant="body2">{job.description}</Typography>
              </Box>
            )}
          </Paper>

          {/* target batches */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
              Target Batches ({targets.length})
            </Typography>
            {targets.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No target batches.</Typography>
            ) : (
              <Stack spacing={1}>
                {targets.map((t) => (
                  <Box
                    key={t.career_job_target_id}
                    sx={{ borderLeft: "2px solid", borderColor: "primary.light", pl: 2 }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {t.course_batch_name || `Batch ${t.course_batch_id}`}
                      {t.course_batch_serial_no ? ` (${t.course_batch_serial_no})` : ""}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(t.project_name || "") + " · " + (t.course_name || "")}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Box>
      )}
    </GlobalWrapper>
  );
};

export default CareerJobDetail;
